import { ColumnsType } from 'antd/es/table';
import { FilterFilled } from '@ant-design/icons';
import { createElement } from 'react';

import { InspectionItemPayload } from '@/services/types/inspection.types';
import { getInspectionItemStatusInfo } from '@/shared/status';
import { ServerFilterDropdown, ServerFilterOption } from './ServerFilterDropdown';

export interface InspectionTableData {
  id: string;
  key: string;
  proposalNumber: string;
  squad?: string | null;
  proponents: string;
  channel: string;
  status: string;
  createdAt: string;
  isFavorite?: boolean;
  items?: InspectionItemPayload[];
  itemCount?: number;
  completionPercentage?: number;
  assignmentId?: string | undefined;
}

interface FilterOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/** Campos filtráveis server-side. */
export type FilterableColumn =
  | 'status'
  | 'inspectionType'
  | 'propertyType'
  | 'inspectionReason'
  | 'provider'
  | 'city';

/** Carregador paginado para campos de texto livre (cidade). */
export type FilterOptionsLoader = (
  field: 'city',
  params: { search: string; pageNumber: number; pageSize: number }
) => Promise<{ items: ServerFilterOption[]; totalCount: number }>;

interface UseInspectionsTableProps {
  inspections: InspectionTableData[];
  onSchedule: (itemId: string, inspectionId: string) => void;
  onAssign: (itemId: string, inspectionId: string) => void;
  onToggleFavorite?: (inspectionId: string) => void;
  // Opções "de cadastro" (lista completa global) para os campos bounded.
  statusOptions?: FilterOption[];
  inspectionTypeOptions?: FilterOption[];
  propertyTypeOptions?: FilterOption[];
  inspectionReasonOptions?: FilterOption[];
  providerOptions?: FilterOption[];
  // Estado dos filtros de coluna ativos (controlado pelo container).
  columnFilters?: Partial<Record<FilterableColumn, string | undefined>>;
  onColumnFilterChange?: (field: FilterableColumn, value: string | undefined) => void;
  // Carregador paginado para campos de texto livre (cidade).
  loadFilterOptions?: FilterOptionsLoader;
}

export function useInspectionsTable({
  onSchedule,
  onAssign,
  onToggleFavorite,
  statusOptions = [],
  inspectionTypeOptions = [],
  propertyTypeOptions = [],
  inspectionReasonOptions = [],
  providerOptions = [],
  columnFilters = {},
  onColumnFilterChange,
  loadFilterOptions,
}: UseInspectionsTableProps) {
  // Monta o filterDropdown server-side para uma coluna. `staticOptions` =>
  // campo de cadastro (busca client-side); `asyncField` => texto livre paginado.
  const buildServerFilter = (
    field: FilterableColumn,
    config: { staticOptions?: FilterOption[]; asyncField?: 'city' }
  ) => {
    const selectedValue = columnFilters[field];
    return {
      filtered: selectedValue !== undefined && selectedValue !== '',
      filterIcon: () =>
        createElement(FilterFilled, {
          style: { color: selectedValue ? '#1890ff' : undefined },
        }),
      filterDropdown: (props: { close: () => void }) =>
        createElement(ServerFilterDropdown, {
          selectedValue,
          close: props.close,
          onChange: (value: string | undefined) => onColumnFilterChange?.(field, value),
          staticOptions: config.staticOptions as ServerFilterOption[] | undefined,
          loadOptions: config.asyncField && loadFilterOptions
            ? (p: { search: string; pageNumber: number; pageSize: number }) =>
                loadFilterOptions(config.asyncField!, p)
            : undefined,
        }),
    };
  };

  const getCompletionPercentage = (items?: InspectionItemPayload[]): number => {
    if (!items || items.length === 0) return 0;

    const completedItems = items.filter((item) => item.status === 9);
    return Math.round((completedItems.length / items.length) * 100);
  };

  const getStatusColorByCode = (statusCode: number | undefined): string => {
    if (statusCode === undefined) return '#d9d9d9';

    switch (statusCode) {
      case 0: return '#ffa500';  // AguardandoDirecionamento
      case 1: return '#1890ff';  // AguardandoAceitePrestadora
      case 2: return '#ff4d4f';  // PrestadoraRecusou
      case 3: return '#722ed1';  // AguardandoDirecionamentoInspetor
      case 4: return '#52c41a';  // AguardandoAceiteInspetor
      case 5: return '#ff4d4f';  // InspetorRecusou
      case 6: return '#faad14';  // AguardandoAgendamento
      case 7: return '#13c2c2';  // Agendado
      case 8: return '#eb2f96';  // CheckinRealizado
      case 9: return '#1890ff';  // Finalizado (AguardandoRevisaoPrestadora)
      case 10: return '#f5222d'; // Cancelado
      case 11: return '#d4380d'; // Frustrado
      case 12: return '#eb2f96'; // AguardandoGeracaoLaudo
      case 13: return '#1890ff'; // AguardandoRevisaoPrestadora
      case 14: return '#fa8c16'; // LaudoNegadoPorPrestadora
      case 15: return '#722ed1'; // AguardandoRevisaoBanco
      case 16: return '#ff4d4f'; // LaudoNegadoPorBanco
      case 17: return '#faad14'; // CorrecaoPeloInspetorBanco
      case 18: return '#52c41a'; // LaudoAceitoPeloBanco
      case 19: return '#722ed1'; // TelevistoriaAgendada
      case 20: return '#13c2c2'; // TelevistoriaFinalizada
      case 21: return '#fa8c16'; // LaudoDevolvido
      case 22: return '#f59e0b'; // AguardandoPreenchimentoPrestadora
      case 30: return '#fa541c'; // DevolvidoPeloBancoAposAceite
      default: return '#d9d9d9';
    }
  };

  const columns: ColumnsType<InspectionTableData> = [
    {
      key: 'priority',
      title: 'Favorito',
      dataIndex: 'isFavorite',
      width: 80,
      render: (_: any, record: InspectionTableData) => {
        return {
          isFavorite: record.isFavorite,
          onToggle: () => onToggleFavorite?.(record.id),
        } as any;
      },
    },
    {
      key: 'proposalNumber',
      title: 'Nº Proposta',
      dataIndex: 'proposalNumber',
      width: 110,
      sorter: (a, b) => a.proposalNumber.localeCompare(b.proposalNumber),
    },
    {
      key: 'squad',
      title: 'Squad',
      dataIndex: 'squad',
      width: 130,
      ellipsis: true,
      render: (squad: string | null | undefined) => squad || '-',
    },
    {
      key: 'proponents',
      title: 'Proponentes',
      dataIndex: 'proponents',
      width: 140,
      ellipsis: true,
    },
    {
      key: 'inspectionType',
      title: 'Tipo Inspeção',
      width: 120,
      ellipsis: true,
      ...buildServerFilter('inspectionType', { staticOptions: inspectionTypeOptions }),
      render: (_: any, record: InspectionTableData) => record.items?.[0]?.inspectionTypeName || '-',
    },
    {
      key: 'propertyType',
      title: 'Tipo Imóvel',
      width: 110,
      ellipsis: true,
      ...buildServerFilter('propertyType', { staticOptions: propertyTypeOptions }),
      render: (_: any, record: InspectionTableData) => record.items?.[0]?.propertyTypeName || '-',
    },
    {
      key: 'inspectionReason',
      title: 'Motivo',
      width: 100,
      ellipsis: true,
      ...buildServerFilter('inspectionReason', { staticOptions: inspectionReasonOptions }),
      render: (_: any, record: InspectionTableData) => record.items?.[0]?.inspectionReasonName || '-',
    },
    {
      key: 'city',
      title: 'Cidade',
      width: 100,
      ellipsis: true,
      ...buildServerFilter('city', { asyncField: 'city' }),
      render: (_: any, record: InspectionTableData) => record.items?.[0]?.city || '-',
    },
    {
      key: 'provider',
      title: 'Prestadora',
      width: 120,
      ellipsis: true,
      ...buildServerFilter('provider', { staticOptions: providerOptions }),
      render: (_: any, record: InspectionTableData) => {
        const assignment = record.items?.[0]?.assignments?.[0];
        return assignment?.providerName || '-';
      },
    },
    {
      key: 'channel',
      title: 'Canal',
      dataIndex: 'channel',
      width: 80,
      ellipsis: true,
    },
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'status',
      width: 200,
      ...buildServerFilter('status', { staticOptions: statusOptions }),
      render: (_: string, record: InspectionTableData) => {
        // Mapeia código de status para cor nomeada do antd (tag soft)
        const getSoftColor = (code: number | undefined): string => {
          if (code === undefined) return 'default';
          // Sucessos / aceitos
          if ([9, 18].includes(code)) return 'green';
          // Em andamento / aguardando
          if ([1, 4, 7, 13, 15].includes(code)) return 'blue';
          // Negados / cancelados
          if ([2, 5, 10, 11, 14, 16].includes(code)) return 'red';
          // Atenção / pendência
          if ([0, 6, 8, 17, 21, 22, 30].includes(code)) return 'orange';
          // Outros / processo
          if ([3, 12, 19, 20].includes(code)) return 'purple';
          return 'default';
        };

        if (record.items && record.items.length > 1) {
          const percentage = getCompletionPercentage(record.items);
          return {
            status: `${percentage}%`,
            color: percentage === 100 ? 'green' : percentage > 0 ? 'blue' : 'orange',
            label: `${percentage}% concluído`,
            isPercentage: true,
          } as any;
        }

        if (record.items && record.items.length === 1) {
          const item = record.items[0];
          if (item && item.status !== undefined) {
            const statusInfo = getInspectionItemStatusInfo(item.status);
            return {
              status: item.status,
              color: getSoftColor(item.status),
              label: statusInfo?.label || item.statusDescription || 'Sem status',
            } as any;
          }
        }

        return {
          status: record.status,
          color: 'default',
          label: record.status || 'Sem status',
        } as any;
      },
    },
    {
      key: 'createdAt',
      title: 'Criação',
      dataIndex: 'createdAt',
      width: 95,
      render: (date: string) => new Date(date).toLocaleDateString('pt-BR'),
      sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      key: 'actions',
      title: 'Ações',
      width: 90,
      fixed: 'right' as const,
      render: (_: any, record: InspectionTableData) => {
        const itemId = record.items?.[0]?.id || record.id;

        return {
          handleSchedule: () => onSchedule(itemId, record.id),
          handleAssign: () => onAssign(itemId, record.id),
        } as any;
      },
    },
  ];

  return {
    columns,
  };
}
